/**
 * Iter 194 — Forex Pro Terminal
 *
 * ForexGPT.ai-comparable feature pack under one roof:
 *   • Sentiment scoring (-100..+100) per asset/TF
 *   • Heatmap Screener — assets × timeframes grid, color-coded
 *   • Smartket-style Multi-TF Scanner (top 2 bull / 2 bear)
 *   • AI Watchlist — custom asset×TF combos, bulk sentiment
 *   • Entry/SL/TP + Risk Sizing
 *   • Session Briefs (US/UK/EU/Asia)
 *   • Puter-powered TTS + AI narratives (client-side, free)
 *
 * All AI narration is client-side via window.puter.ai.chat / puter.ai.txt2speech.
 * No Emergent LLM key is consumed by this page.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Target, Flame, ListChecks, Globe2, Volume2, Loader2,
  TrendingUp, TrendingDown, Scan, Play, Pause, Sparkles,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { chatText, hasPuter } from "../services/puterAI";
import { Zap } from "lucide-react";

const USER_ID = "default";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/forex-pro`;

const TF_OPTIONS = ["5s", "1m", "5m", "15m", "30m", "1h", "4h"];
const DEFAULT_WATCHLIST = [
  "EURUSD_OTC", "GBPUSD_OTC", "USDJPY_OTC", "AUDUSD_OTC",
  "USDCAD_OTC", "USDCHF_OTC", "NZDUSD_OTC", "EURGBP_OTC",
];

/* ────────────────────────── Helpers ────────────────────────── */
function sentimentColor(s) {
  const v = Number(s);
  if (!Number.isFinite(v)) return "bg-slate-800 text-slate-400";
  if (v >= 70)  return "bg-emerald-600 text-white";
  if (v >= 40)  return "bg-emerald-800 text-emerald-100";
  if (v >= 15)  return "bg-emerald-950 text-emerald-200";
  if (v <= -70) return "bg-rose-600 text-white";
  if (v <= -40) return "bg-rose-800 text-rose-100";
  if (v <= -15) return "bg-rose-950 text-rose-200";
  return "bg-slate-800 text-slate-400";
}

function SentimentPill({ score, label }) {
  const v = Number(score ?? 0);
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${sentimentColor(v)}`}
         data-testid="fp-sentiment-pill">
      {v >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {label ? `${label} ` : ""}{v.toFixed(1)}
    </div>
  );
}

/* ────────────────────────── 1. Sentiment Panel ────────────────────────── */
function SentimentPanel({ asset, timeframe }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/sentiment/score`,
        { asset, timeframe, candle_limit: 400 },
        { timeout: 30000 });
      if (myId === reqIdRef.current) setScore(r.data);
    } catch (e) {
      if (myId === reqIdRef.current) toast.error(e?.response?.data?.detail || e.message);
    } finally { if (myId === reqIdRef.current) setLoading(false); }
  }, [asset, timeframe]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !score) {
    return <div className="text-slate-500 text-sm flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> Scoring {asset}@{timeframe}…
    </div>;
  }
  if (!score?.ok) return <div className="text-slate-500 text-sm">{score?.reason || "–"}</div>;

  const comps = score.components || {};
  return (
    <Card className="bg-slate-900/70 border-slate-800" data-testid="fp-sentiment-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
          <Activity className="h-4 w-4 text-amber-400"/> Sentiment · {asset} <span className="text-slate-500 text-xs">{timeframe}</span>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={refresh} data-testid="fp-sentiment-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "↻"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-3">
          <span className={`text-4xl font-bold ${score.sentiment >= 0 ? "text-emerald-400" : "text-rose-400"}`}
                data-testid="fp-sentiment-value">
            {score.sentiment >= 0 ? "+" : ""}{score.sentiment.toFixed(1)}
          </span>
          <Badge variant="outline" className="border-slate-700 text-slate-300">
            {score.magnitude} {score.direction}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
          <div>Tech vote: <b className="text-slate-200">{(comps.tech_vote*100 || 0).toFixed(0)}</b></div>
          <div>GodMode: <b className="text-slate-200">{(comps.godmode*100 || 0).toFixed(0)}</b></div>
          <div>Setup: <b className="text-slate-200">{(comps.setup*100 || 0).toFixed(0)}</b></div>
          <div>Momentum: <b className="text-slate-200">{(comps.momentum*100 || 0).toFixed(0)}</b></div>
        </div>
        {score.best_setup && (
          <div className="text-xs text-amber-300 border-t border-slate-800 pt-2">
            Best setup: <b>{score.best_setup.name}</b> @ {(score.best_setup.confidence * 100).toFixed(0)}% ({score.best_setup.direction})
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ────────────────────────── 2. Heatmap Screener ────────────────────────── */
function HeatmapTab() {
  const [assets, setAssets] = useState(DEFAULT_WATCHLIST);
  const [tfs, setTfs] = useState(["5s", "1m"]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/heatmap`,
        { assets, timeframes: tfs, candle_limit: 300 },
        { timeout: 120000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-4" data-testid="fp-heatmap-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[300px]">
          <Label className="text-slate-400 text-xs">Assets (comma-sep)</Label>
          <Input value={assets.join(",")} onChange={(e) => setAssets(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-heatmap-assets" />
        </div>
        <div>
          <Label className="text-slate-400 text-xs">Timeframes</Label>
          <div className="flex gap-1 mt-1">
            {TF_OPTIONS.map(tf => (
              <button key={tf} onClick={() => setTfs(prev => prev.includes(tf) ? prev.filter(t => t !== tf) : [...prev, tf])}
                      data-testid={`fp-heatmap-tf-${tf}`}
                      className={`px-2 py-1 rounded text-xs font-mono ${tfs.includes(tf) ? "bg-amber-500 text-slate-900" : "bg-slate-800 text-slate-400"}`}>
                {tf}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={run} disabled={loading || !tfs.length} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                data-testid="fp-heatmap-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Scan className="h-4 w-4 mr-1"/>}
          Run Heatmap
        </Button>
      </div>

      {data?.ok && (
        <div className="overflow-auto border border-slate-800 rounded-lg">
          <table className="w-full text-sm" data-testid="fp-heatmap-grid">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Asset</th>
                {data.timeframes.map(tf => <th key={tf} className="px-3 py-2 text-center">{tf}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => (
                <tr key={row.asset} className="border-t border-slate-800">
                  <td className="px-3 py-2 text-slate-200 font-mono">{row.asset}</td>
                  {data.timeframes.map(tf => {
                    const cell = row.cells[tf];
                    if (!cell?.ok) return <td key={tf} className="px-3 py-2 text-center text-slate-600">–</td>;
                    return (
                      <td key={tf} className="px-1 py-1 text-center"
                          data-testid={`fp-heatmap-cell-${row.asset}-${tf}`}>
                        <div className={`inline-flex flex-col px-2 py-1 rounded ${sentimentColor(cell.sentiment)}`}
                             title={cell.best_setup ? `Setup: ${cell.best_setup}` : ""}>
                          <span className="text-sm font-bold">{cell.sentiment >= 0 ? "+" : ""}{cell.sentiment.toFixed(0)}</span>
                          <span className="text-[10px] opacity-80">{cell.magnitude}</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── 3. Smartket Multi-TF Scanner ────────────────────────── */
function SmartketScannerTab({ onPick }) {
  const [assets, setAssets] = useState(DEFAULT_WATCHLIST.concat(["EURJPY_OTC", "GBPJPY_OTC", "AUDJPY_OTC", "AUDNZD_OTC"]));
  const [tfs, setTfs] = useState(["5s", "1m", "15m"]);
  const [topN, setTopN] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function scan() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/smartket-scan`,
        { assets, timeframes: tfs.slice(0, 4), top_n: topN },
        { timeout: 180000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-4" data-testid="fp-smartket-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[300px]">
          <Label className="text-slate-400 text-xs">Assets</Label>
          <Input value={assets.join(",")} onChange={(e) => setAssets(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-smartket-assets"/>
        </div>
        <div>
          <Label className="text-slate-400 text-xs">Timeframes (max 4)</Label>
          <div className="flex gap-1 mt-1">
            {TF_OPTIONS.map(tf => (
              <button key={tf} onClick={() => setTfs(prev => prev.includes(tf) ? prev.filter(t => t !== tf) : [...prev, tf].slice(0, 4))}
                      data-testid={`fp-smartket-tf-${tf}`}
                      className={`px-2 py-1 rounded text-xs font-mono ${tfs.includes(tf) ? "bg-amber-500 text-slate-900" : "bg-slate-800 text-slate-400"}`}>
                {tf}
              </button>
            ))}
          </div>
        </div>
        <div className="w-16">
          <Label className="text-slate-400 text-xs">Top N</Label>
          <Input type="number" min="1" max="10" value={topN} onChange={(e) => setTopN(Math.max(1, Math.min(10, +e.target.value || 2)))}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1 font-mono" data-testid="fp-smartket-topn"/>
        </div>
        <Button onClick={scan} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                data-testid="fp-smartket-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Flame className="h-4 w-4 mr-1"/>}
          Smartket Scan
        </Button>
      </div>

      {data?.ok && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-900/70 border-emerald-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-300 flex items-center gap-2">
              <TrendingUp className="h-4 w-4"/> Top Bullish
            </CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data.top_bullish || []).map(r => (
                <div key={r.asset} className="flex items-center justify-between p-2 bg-emerald-950/30 rounded hover:bg-emerald-900/40 cursor-pointer"
                     onClick={() => onPick && onPick(r.asset)} data-testid={`fp-smartket-bull-${r.asset}`}>
                  <div>
                    <div className="font-mono text-slate-200">{r.asset}</div>
                    <div className="text-xs text-slate-500">
                      {r.all_tfs_agree && <span className="text-amber-400 mr-1">✓ aligned</span>}
                      {Object.keys(r.per_timeframe || {}).join(" / ")}
                    </div>
                  </div>
                  <SentimentPill score={r.composite_sentiment} />
                </div>
              ))}
              {!(data.top_bullish || []).length && <div className="text-slate-500 text-xs">No bullish setups</div>}
            </CardContent>
          </Card>
          <Card className="bg-slate-900/70 border-rose-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-rose-300 flex items-center gap-2">
              <TrendingDown className="h-4 w-4"/> Top Bearish
            </CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data.top_bearish || []).map(r => (
                <div key={r.asset} className="flex items-center justify-between p-2 bg-rose-950/30 rounded hover:bg-rose-900/40 cursor-pointer"
                     onClick={() => onPick && onPick(r.asset)} data-testid={`fp-smartket-bear-${r.asset}`}>
                  <div>
                    <div className="font-mono text-slate-200">{r.asset}</div>
                    <div className="text-xs text-slate-500">
                      {r.all_tfs_agree && <span className="text-amber-400 mr-1">✓ aligned</span>}
                      {Object.keys(r.per_timeframe || {}).join(" / ")}
                    </div>
                  </div>
                  <SentimentPill score={r.composite_sentiment} />
                </div>
              ))}
              {!(data.top_bearish || []).length && <div className="text-slate-500 text-xs">No bearish setups</div>}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── 4. AI Watchlist (bulk) ────────────────────────── */
function WatchlistTab({ onPick }) {
  const [combos, setCombos] = useState(() => DEFAULT_WATCHLIST.flatMap(a => [{ asset: a, timeframe: "5s" }, { asset: a, timeframe: "1m" }]));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function scan() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/watchlist/scan`,
        { combos: combos.slice(0, 50), candle_limit: 300 },
        { timeout: 180000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-4" data-testid="fp-watchlist-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-400">{combos.length} combos</div>
        <Button onClick={scan} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                data-testid="fp-watchlist-scan">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <ListChecks className="h-4 w-4 mr-1"/>}
          Scan Watchlist
        </Button>
      </div>

      {data?.ok && (
        <div className="overflow-auto border border-slate-800 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Asset</th>
                <th className="px-3 py-2 text-left">TF</th>
                <th className="px-3 py-2 text-left">Sentiment</th>
                <th className="px-3 py-2 text-left">Setup</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/50">
                  <td className="px-3 py-2 text-slate-200 font-mono">{r.asset}</td>
                  <td className="px-3 py-2 text-slate-400 font-mono text-xs">{r.timeframe}</td>
                  <td className="px-3 py-2">{r.ok ? <SentimentPill score={r.sentiment} label={r.direction}/> : <span className="text-slate-600">{r.reason}</span>}</td>
                  <td className="px-3 py-2 text-xs text-amber-300">{r.best_setup?.name || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onPick && onPick(r.asset, r.timeframe)}
                            data-testid={`fp-watchlist-pick-${r.asset}-${r.timeframe}`}>
                      <Sparkles className="h-3 w-3"/>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── 5. Levels / Risk ────────────────────────── */
function LevelsTab({ asset, timeframe }) {
  const [balance, setBalance] = useState(1000);
  const [risk, setRisk] = useState(1.0);
  const [rr, setRr] = useState(2.0);
  const [expirySec, setExpirySec] = useState(60);
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firing, setFiring] = useState(null);     // "BUY" | "SELL" | null
  const [lastFire, setLastFire] = useState(null);

  async function compute() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/levels`,
        { asset, timeframe, candle_limit: 400,
          account_balance: balance, risk_pct: risk, rr_ratio: rr },
        { timeout: 30000 });
      setOut(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  async function fire(side) {
    if (!out?.ok) {
      toast.error("Compute levels first");
      return;
    }
    setFiring(side);
    const direction = side === "BUY" ? "CALL" : "PUT";
    const stake = Math.max(1, Math.round(out.risk.risk_amount));
    const tradeId = `fp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/one-tap/fire`,
        {
          user_id: USER_ID,
          asset,
          direction,
          amount: stake,
          expiry_s: expirySec,
          confidence: Math.min(1, Math.abs(out.sentiment) / 100),
          source: "forex_pro_terminal",
          trade_id: tradeId,
          client_ts: Date.now(),
          setup: {
            origin: "forex_pro_levels",
            side,
            entry: out.entry,
            stop_loss: out.stop_loss,
            take_profit: out.take_profit,
            adaptive_tp: out.adaptive_tp_from_sr,
            rr_ratio: rr,
            sentiment: out.sentiment,
            atr_pct: out.atr_pct,
            position_units: out.risk.position_units,
          },
          profile_tf: timeframe,
        },
        { timeout: 20000 },
      );
      if (resp.data?.ok) {
        setLastFire({
          side, direction,
          trade_id: tradeId,
          stake,
          placed_at: new Date().toISOString(),
          exec_note: resp.data?.exec_note || resp.data?.note || "fired",
        });
        toast.success(`${side} fired · ${asset} · $${stake} · ${expirySec}s`);
      } else {
        toast.error(`Fire blocked: ${resp.data?.reason || "unknown"}`);
        setLastFire({ side, blocked: true, reason: resp.data?.reason, placed_at: new Date().toISOString() });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setFiring(null);
    }
  }

  const canFire = out?.ok && !firing;
  return (
    <div className="space-y-3" data-testid="fp-levels-tab">
      <div className="grid grid-cols-4 gap-3">
        <div>
          <Label className="text-slate-400 text-xs">Account ($)</Label>
          <Input type="number" value={balance} onChange={(e) => setBalance(+e.target.value || 1000)}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-levels-balance"/>
        </div>
        <div>
          <Label className="text-slate-400 text-xs">Risk %</Label>
          <Input type="number" step="0.1" value={risk} onChange={(e) => setRisk(+e.target.value || 1)}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-levels-risk"/>
        </div>
        <div>
          <Label className="text-slate-400 text-xs">Reward:Risk</Label>
          <Input type="number" step="0.5" value={rr} onChange={(e) => setRr(+e.target.value || 2)}
                 className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-levels-rr"/>
        </div>
        <div>
          <Label className="text-slate-400 text-xs">Expiry (sec)</Label>
          <Select value={String(expirySec)} onValueChange={(v) => setExpirySec(+v)}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 mt-1" data-testid="fp-levels-expiry">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {[15, 30, 60, 120, 180, 300, 600, 900].map(s =>
                <SelectItem key={s} value={String(s)}>{s < 60 ? `${s}s` : `${s/60}m`}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button onClick={compute} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold w-full"
              data-testid="fp-levels-compute">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Target className="h-4 w-4 mr-1"/>}
        Compute Levels for {asset} · {timeframe}
      </Button>
      {out?.ok && (
        <Card className="bg-slate-900/70 border-slate-800">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Badge className={out.direction === "BUY" ? "bg-emerald-600" : "bg-rose-600"} data-testid="fp-levels-dir">
                {out.direction}
              </Badge>
              <SentimentPill score={out.sentiment}/>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm font-mono">
              <div><span className="text-slate-500 text-xs">Entry</span><div className="text-slate-100" data-testid="fp-levels-entry">{out.entry}</div></div>
              <div><span className="text-slate-500 text-xs">Stop Loss</span><div className="text-rose-300" data-testid="fp-levels-sl">{out.stop_loss}</div></div>
              <div><span className="text-slate-500 text-xs">Take Profit (RR)</span><div className="text-emerald-300" data-testid="fp-levels-tp">{out.take_profit}</div></div>
              <div><span className="text-slate-500 text-xs">Adaptive TP (S/R)</span><div className="text-amber-300">{out.adaptive_tp_from_sr ?? "—"}</div></div>
            </div>
            <div className="border-t border-slate-800 pt-2 text-xs text-slate-400 space-y-1">
              <div>Risk: ${out.risk.risk_amount} ({out.risk.risk_pct}% of ${out.risk.account_balance})</div>
              <div>Position size: <b className="text-slate-200">{out.risk.position_units.toLocaleString()} units</b></div>
              <div>SL distance: {out.sl_distance_pct}% ({out.sl_distance_abs})</div>
              <div>ATR%: {out.atr_pct}</div>
            </div>
            {/* Fire buttons — pipe through One-Tap */}
            <div className="border-t border-slate-800 pt-3 grid grid-cols-2 gap-3">
              <Button
                onClick={() => fire("BUY")}
                disabled={!canFire}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 text-base"
                data-testid="fp-levels-fire-buy"
              >
                {firing === "BUY"
                  ? <Loader2 className="h-5 w-5 animate-spin mr-2"/>
                  : <Zap className="h-5 w-5 mr-2"/>}
                Fire BUY · ${Math.max(1, Math.round(out.risk.risk_amount))} · {expirySec < 60 ? `${expirySec}s` : `${expirySec/60}m`}
              </Button>
              <Button
                onClick={() => fire("SELL")}
                disabled={!canFire}
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold h-12 text-base"
                data-testid="fp-levels-fire-sell"
              >
                {firing === "SELL"
                  ? <Loader2 className="h-5 w-5 animate-spin mr-2"/>
                  : <Zap className="h-5 w-5 mr-2"/>}
                Fire SELL · ${Math.max(1, Math.round(out.risk.risk_amount))} · {expirySec < 60 ? `${expirySec}s` : `${expirySec/60}m`}
              </Button>
            </div>
            {lastFire && (
              <div className={`mt-2 p-2 rounded text-xs font-mono ${lastFire.blocked ? "bg-rose-950/50 text-rose-300 border border-rose-800" : "bg-emerald-950/50 text-emerald-300 border border-emerald-800"}`}
                   data-testid="fp-levels-last-fire">
                {lastFire.blocked
                  ? `✗ ${lastFire.side} blocked · ${lastFire.reason}`
                  : `✓ ${lastFire.side} fired · trade ${lastFire.trade_id} · $${lastFire.stake} · ${lastFire.exec_note}`}
                <span className="text-slate-500 ml-2">{new Date(lastFire.placed_at).toLocaleTimeString()}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ────────────────────────── 6. Session Briefs ────────────────────────── */
function SessionBriefsTab() {
  const [session, setSession] = useState("us");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [narrating, setNarrating] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  async function load() {
    setLoading(true); setNarrative("");
    try {
      const r = await axios.get(`${API}/session-brief/${session}`,
        { params: { timeframes: "5s,1m" }, timeout: 180000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  async function narrate() {
    if (!data?.ok || !hasPuter()) {
      toast.error("Puter not loaded — can't narrate");
      return;
    }
    setNarrating(true);
    try {
      const prompt = [
        `You are a concise FX desk analyst. Write a 3-paragraph ${data.session} session brief:`,
        "Para 1: top bullish setups and why.",
        "Para 2: top bearish setups and why.",
        "Para 3: single risk-weighted recommendation.",
        "Max 450 chars total. No disclaimers.",
        `Bullish: ${JSON.stringify((data.top_bullish || []).map(r => [r.asset, r.composite_sentiment]))}`,
        `Bearish: ${JSON.stringify((data.top_bearish || []).map(r => [r.asset, r.composite_sentiment]))}`,
      ].join("\n");
      const text = await chatText(prompt);
      setNarrative(String(text || "").slice(0, 1500));
    } catch (e) { toast.error(`Puter: ${e.message || e}`); }
    finally { setNarrating(false); }
  }

  async function speak() {
    if (!narrative || !window.puter?.ai?.txt2speech) {
      toast.info("Narrate first, then Puter will read it aloud.");
      return;
    }
    setSpeaking(true);
    try {
      const audio = await window.puter.ai.txt2speech(narrative);
      if (audio?.play) await audio.play();
    } catch (e) { toast.error(`TTS: ${e.message || e}`); }
    finally { setSpeaking(false); }
  }

  return (
    <div className="space-y-4" data-testid="fp-sessions-tab">
      <div className="flex items-center gap-2">
        <Select value={session} onValueChange={setSession}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="fp-sessions-select">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="us">US Session (13-21 UTC)</SelectItem>
            <SelectItem value="uk">UK Session (08-16 UTC)</SelectItem>
            <SelectItem value="eu">EU Session (07-15 UTC)</SelectItem>
            <SelectItem value="asia">Asia Session (00-08 UTC)</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                data-testid="fp-sessions-load">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Globe2 className="h-4 w-4 mr-1"/>}
          Build Brief
        </Button>
        {data?.ok && <Button onClick={narrate} disabled={narrating || !hasPuter()} variant="outline"
                              className="border-slate-700" data-testid="fp-sessions-narrate">
          {narrating ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Sparkles className="h-4 w-4 mr-1"/>}
          Narrate (Puter)
        </Button>}
        {narrative && <Button onClick={speak} disabled={speaking} variant="outline"
                                className="border-slate-700" data-testid="fp-sessions-tts">
          {speaking ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Volume2 className="h-4 w-4 mr-1"/>}
          Read aloud
        </Button>}
      </div>

      {data?.ok && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-900/70 border-emerald-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-300">Top Bullish</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data.top_bullish || []).map(r => (
                <div key={r.asset} className="flex items-center justify-between p-2 bg-emerald-950/30 rounded">
                  <span className="font-mono text-slate-200">{r.asset}</span>
                  <SentimentPill score={r.composite_sentiment}/>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="bg-slate-900/70 border-rose-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-rose-300">Top Bearish</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data.top_bearish || []).map(r => (
                <div key={r.asset} className="flex items-center justify-between p-2 bg-rose-950/30 rounded">
                  <span className="font-mono text-slate-200">{r.asset}</span>
                  <SentimentPill score={r.composite_sentiment}/>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
      {narrative && (
        <Card className="bg-slate-900/70 border-amber-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-300 flex items-center gap-2">
            <Sparkles className="h-4 w-4"/> Puter Narrative
          </CardTitle></CardHeader>
          <CardContent className="text-sm text-slate-300 whitespace-pre-wrap" data-testid="fp-sessions-narrative">
            {narrative}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ────────────────────────── Main Terminal ────────────────────────── */
export default function ForexProTerminal() {
  const [activeAsset, setActiveAsset] = useState("EURUSD_OTC");
  const [activeTf, setActiveTf] = useState("5s");

  function pickAsset(asset, tf) {
    setActiveAsset(asset);
    if (tf) setActiveTf(tf);
    toast.success(`Focused ${asset}${tf ? " @ " + tf : ""}`);
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6" data-testid="forex-pro-terminal">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Flame className="h-7 w-7 text-amber-400"/> Forex Pro Terminal
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Sentiment · Heatmap · Smartket Scanner · AI Watchlist · Entry/SL/TP · Session Briefs · Puter TTS
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={activeAsset} onValueChange={setActiveAsset}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="fp-active-asset">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
              {DEFAULT_WATCHLIST.concat(["EURJPY_OTC", "GBPJPY_OTC", "AUDJPY_OTC", "AUDNZD_OTC", "EURAUD_OTC"]).map(a =>
                <SelectItem key={a} value={a}>{a}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select value={activeTf} onValueChange={setActiveTf}>
            <SelectTrigger className="w-24 bg-slate-900 border-slate-700 text-slate-200" data-testid="fp-active-tf">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {TF_OPTIONS.map(tf => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <Tabs defaultValue="heatmap" className="w-full">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger value="heatmap" data-testid="fp-tab-heatmap"><Activity className="h-4 w-4 mr-1"/>Heatmap</TabsTrigger>
              <TabsTrigger value="smartket" data-testid="fp-tab-smartket"><Flame className="h-4 w-4 mr-1"/>Smartket Scanner</TabsTrigger>
              <TabsTrigger value="watchlist" data-testid="fp-tab-watchlist"><ListChecks className="h-4 w-4 mr-1"/>AI Watchlist</TabsTrigger>
              <TabsTrigger value="levels" data-testid="fp-tab-levels"><Target className="h-4 w-4 mr-1"/>Entry/SL/TP</TabsTrigger>
              <TabsTrigger value="sessions" data-testid="fp-tab-sessions"><Globe2 className="h-4 w-4 mr-1"/>Sessions</TabsTrigger>
            </TabsList>
            <TabsContent value="heatmap" className="mt-4"><HeatmapTab/></TabsContent>
            <TabsContent value="smartket" className="mt-4"><SmartketScannerTab onPick={pickAsset}/></TabsContent>
            <TabsContent value="watchlist" className="mt-4"><WatchlistTab onPick={pickAsset}/></TabsContent>
            <TabsContent value="levels" className="mt-4"><LevelsTab asset={activeAsset} timeframe={activeTf}/></TabsContent>
            <TabsContent value="sessions" className="mt-4"><SessionBriefsTab/></TabsContent>
          </Tabs>
        </div>
        <div className="space-y-4">
          <SentimentPanel asset={activeAsset} timeframe={activeTf}/>
          <Card className="bg-slate-900/70 border-slate-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Puter Status</CardTitle></CardHeader>
            <CardContent className="text-xs text-slate-400 space-y-1">
              <div>
                AI Narratives & TTS: {hasPuter() ? <span className="text-emerald-400">✓ ready</span> : <span className="text-rose-400">not loaded</span>}
              </div>
              <div>All deep analysis runs on your Puter account (free credits).</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
